import { useState } from 'react';

export interface StandingErrors<Field extends string> {
  /** The server's refusals the user has not answered yet by editing that field. */
  standing: Partial<Record<Field, string>>;
  dismiss: (field: Field) => void;
}

/**
 * The server's field errors, each shown until its field is edited. A new set
 * of errors from the server brings them all back.
 */
export function useStandingErrors<Field extends string>(
  serverErrors: Partial<Record<Field, string>>,
): StandingErrors<Field> {
  const [judged, setJudged] = useState(serverErrors);
  const [dismissed, setDismissed] = useState<ReadonlySet<Field>>(new Set());
  if (judged !== serverErrors) {
    setJudged(serverErrors);
    setDismissed(new Set());
  }

  const standing: Partial<Record<Field, string>> = {};
  for (const field of Object.keys(serverErrors) as Field[]) {
    if (!dismissed.has(field)) standing[field] = serverErrors[field];
  }

  const dismiss = (field: Field) => {
    setDismissed((current) =>
      current.has(field) ? current : new Set([...current, field]),
    );
  };

  return { standing, dismiss };
}
